// TIP-609 — the single seam between this page and its data.
//
// It now calls the allowance endpoint for the company selected in the
// dashboard switcher and returns the same shape the fixtures returned, so no
// component below it changed.
//
// The ?cbState=<key> override is KEPT. It short-circuits the request and
// resolves fixtures instead, which is the only way to put all ten paths on
// screen for review — the backend can produce one state per account per month.
// It is a review affordance, not a fallback: without the parameter the page is
// entirely driven by the payload.
//
// Two things are decided before any request goes out:
//   * no company selected  → the unclaimed gate, nothing to ask about
//   * a companyID with no TRX prefix → an unclaimed directory listing, which
//     the endpoint answers 404 for. The gate renders without the round trip.
// A 404 that arrives anyway is treated identically, because that is what the
// backend uses to say "not claimed".
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ALL_STATUSES, BRIEFS, BRIEF_STATUSES, BUSINESS, TABLE_COPY } from "./briefsFixtures";
import { ALL_STATES, GATE_STATES, DEFAULT_STATE, deriveAllowance } from "./briefsStates";
import { isClaimedCompanyId, mapAllowanceResponse, mapBriefDetail } from "./briefsApiMapping";
import { buildOrderPayload, describeOrderError } from "./orderPayload";
import { mapRequesterProfile } from "./requesterProfile";
import { mapRequesterLookup } from "./requesterLookup";
import { duplicateWarning, isSearchable } from "./duplicateSubject";
import { MEMBERSHIP_COPY } from "./membershipCheckoutCopy";
import { createVerifiedCheckoutSession } from "../../../public/VerifyAccount/api";
import { buildDownloadFilename, describeDownloadError, saveBlob } from "./briefDownload";
import {
  useCreateBriefOrderMutation,
  useGetBriefDetailQuery,
  useGetBriefsAllowanceQuery,
  useGetBriefsListQuery,
  useLazyLookupBriefRequesterQuery,
  useDownloadBriefOrderMutation,
  useCreateBriefCheckoutSessionMutation,
  useLazyGetBriefPurchaseStateQuery,
  useLazyResolveBriefReferenceQuery,
  useLazyGetBriefsListQuery,
} from "../../../../store/briefs/briefsApiSlice";
import { selectActiveCompanyId, setActiveCompany } from "../../../../store/activeCompanySlice";
import { selectBriefPricing } from "../../../../store/briefs/briefPricingSlice";
import { useGetUserQuery } from "../../../../store/auth/usersApiSlice";
import {
  useGetIndividualQuery,
  useGetOptimisedCompanyDetailsQuery,
} from "../../../../store/Company/companyApiSlice";

const NO_ROWS = [];

// Long enough that typing a company name is one request, short enough that
// the table does not feel stuck. Matches the admin queue's own debounce.
const SEARCH_DEBOUNCE_MS = 350;

// TIP-614 — shown when the backend gives us nothing to quote. It never
// promises the payment failed, because at session-create time nothing has been
// charged either way.
const ORDER_SUPPORT_FALLBACK =
  "We could not start checkout. Nothing has been charged.";

const countByStatus = (briefs) =>
  briefs.reduce((acc, b) => ({ ...acc, [b.status]: (acc[b.status] || 0) + 1 }), {});

export const useCounterpartyBriefs = () => {
  const { search } = useLocation();
  const companyID = useSelector(selectActiveCompanyId);
  const token = useSelector((s) => s.auth?.token);

  const fixtureState = useMemo(() => {
    const requested = new URLSearchParams(search).get("cbState");
    return ALL_STATES.includes(requested) ? requested : null;
  }, [search]);

  // A listing that cannot be claimed is answered locally; so is a page opened
  // with no company selected at all.
  const askable = !fixtureState && isClaimedCompanyId(companyID);

  const { data, error, isLoading, isFetching, refetch } = useGetBriefsAllowanceQuery(
    { companyID, token },
    { skip: !askable }
  );

  // TIP-610 — the table's page, search and status filter. Held HERE rather
  // than inside BriefsTable, because each one is now part of the request: the
  // backend pages, searches and filters, so the table cannot own state the
  // query needs.
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  // What is typed, and what has been sent. Separate, so the input stays
  // responsive while the request waits for the typing to settle.
  const [query, setQuery] = useState("");
  const [sentQuery, setSentQuery] = useState("");

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed === sentQuery) return undefined;
    const timer = setTimeout(() => {
      setSentQuery(trimmed);
      // A narrowed set has different pages. Staying on page 4 of a result that
      // now has one page would show an empty table.
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, sentQuery]);

  // Switching company replaces the whole result set, so the page and the
  // filter cannot carry over.
  useEffect(() => {
    setPage(1);
    setStatusFilter(ALL_STATUSES);
    setQuery("");
    setSentQuery("");
  }, [companyID]);

  // TIP-610 — the rows. Read alongside the allowance rather than after it: the
  // table and the card are independent reads, so a slow list must not hold the
  // counters off the screen, and a failed list must not blank the card.
  const list = useGetBriefsListQuery(
    {
      companyID,
      token,
      page,
      search: sentQuery,
      // ALL_STATUSES is the frontend's own sentinel for "no filter"; the
      // parameter is left off rather than sent as a status the backend would
      // reject.
      status: statusFilter === ALL_STATUSES ? "" : statusFilter,
    },
    { skip: !askable }
  );

  const onQuery = useCallback((value) => setQuery(value), []);
  const onStatusFilter = useCallback((value) => {
    setStatusFilter(value);
    setPage(1);
  }, []);
  const onPage = useCallback((next) => setPage(Math.max(1, Number(next) || 1)), []);

  return useMemo(() => {
    // ---- review path: fixtures, driven by ?cbState= -----------------------
    if (fixtureState) {
      const isGate = GATE_STATES.includes(fixtureState);
      const briefs = isGate || fixtureState === "brand-new-account" ? NO_ROWS : BRIEFS;
      return {
        state: fixtureState,
        isGate,
        gateVariant: isGate ? fixtureState : null,
        business: BUSINESS,
        allowance: isGate ? null : deriveAllowance(fixtureState),
        briefs,
        statusCounts: countByStatus(briefs),
        statuses: Object.values(BRIEF_STATUSES),
        isLoading: false,
        isFetching: false,
        error: null,
        refetch: () => {},
        briefsLoading: false,
        briefsError: null,
        refetchBriefs: () => {},
      };
    }

    // ---- live path --------------------------------------------------------
    const notFound = !askable || error?.status === 404;
    // A 404 is the unclaimed answer, not a failure. Everything else — 500, a
    // dropped connection — is a real error and the page says so rather than
    // showing an empty allowance that reads as "you have none left", or a
    // claim gate that reads as "you never claimed this business".
    const realError = error && error.status !== 404 ? error : null;
    const pending = askable && isLoading;

    const resolved =
      realError || pending
        ? { isGate: false, gateVariant: null, business: null, allowance: null }
        : mapAllowanceResponse(data, { notFound });

    return {
      // The backend's own state name where there is one; the fixture keys never
      // appear on the live path.
      state: resolved.allowance?.apiState ?? resolved.gateVariant ?? DEFAULT_STATE,
      ...resolved,
      // An account with nothing ordered comes back with an empty `results`,
      // and the table draws its own empty state from that — no special flag.
      briefs: list.data?.briefs ?? NO_ROWS,
      // From the backend's own status_counts, over the whole unfiltered set.
      statusCounts: list.data?.statusCounts ?? {},
      statuses: Object.values(BRIEF_STATUSES),
      // Everything the table needs to be server-driven. Its presence is what
      // switches the table out of its own client-side filtering — the
      // ?cbState= review path passes none of this and keeps that behaviour.
      server: {
        query,
        onQuery,
        status: statusFilter,
        onStatusFilter,
        page: list.data?.currentPage ?? page,
        pageCount: list.data?.totalPages ?? 1,
        onPage,
        // The FILTERED count, for "Showing 1 to 10 of N".
        total: list.data?.total ?? 0,
        // Every brief this company has ordered — the number beside "All
        // statuses", and what decides whether the empty state belongs on
        // screen rather than "no briefs match this view".
        totalAll: list.data?.totalAll ?? 0,
        // A search or a filter change refetches; the rows on screen are the
        // previous answer until it lands.
        isFetching: askable && list.isFetching,
      },
      briefsLoading: askable && list.isLoading,
      // A list that fails is reported on its own. The allowance card stays up:
      // the customer can still see what they have left and still order.
      briefsError: list.error ?? null,
      refetchBriefs: list.refetch,
      // The page mounts the card and the table only once this is false, so
      // neither ever renders against a half-resolved payload.
      isLoading: pending,
      isFetching: askable && isFetching,
      error: realError,
      refetch,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fixtureState, askable, data, error, isLoading, isFetching, refetch, list,
    query, statusFilter, page, onQuery, onStatusFilter, onPage,
  ]);
};

/**
 * TIP-610 — one brief's detail, for the status modal.
 *
 * Takes the row the customer clicked and returns it enriched, so the modal
 * renders from the row immediately and fills in the requester, the purpose,
 * the customer's note and the source bucket when the detail lands. Nothing
 * blocks on the request: a modal that opens empty and waits would be worse
 * than one that opens with what the table already knows.
 *
 * Passing a fixture row (the ?cbState= path) makes no request.
 */
export const useBriefDetail = (brief, { live = true } = {}) => {
  const token = useSelector((s) => s.auth?.token);
  const reference = brief?.reference ?? null;

  const { data, isLoading, error } = useGetBriefDetailQuery(
    { reference, token },
    { skip: !live || !reference }
  );

  return useMemo(() => {
    if (!brief) return null;
    const detail = mapBriefDetail(data);
    return {
      ...brief,
      // The row's own status wins over the detail's: they come from the same
      // record, and the row is what the customer just clicked on.
      detail,
      detailLoading: live && !!reference && isLoading,
      detailError: error ?? null,
    };
  }, [brief, data, isLoading, error, live, reference]);
};

export default useCounterpartyBriefs;

/**
 * TIP-611 — placing an order.
 *
 * Returns the `onSubmit` the order modal expects: it is handed the form's
 * values plus the idempotency key, and it answers with either the created
 * brief or a described failure. The modal never sees an HTTP status.
 *
 * The allowance is read from the store rather than passed in, so the
 * `expected_source` sent to the backend is what the CARD says right now — not
 * what it said when the modal mounted.
 */
export const useSubmitBriefOrder = () => {
  const companyID = useSelector(selectActiveCompanyId);
  const token = useSelector((s) => s.auth?.token);
  const [createOrder, { isLoading }] = useCreateBriefOrderMutation();

  const submit = useCallback(
    async ({ idempotencyKey, allowance, ...values }) => {
      // Nothing to order against. The page cannot reach this state — the modal
      // only mounts behind a claimed allowance — but a submit that silently
      // did nothing would be worse than one that says so.
      if (!companyID) {
        return { error: describeOrderError({ status: 404 }) };
      }

      const payload = buildOrderPayload({ values, idempotencyKey, companyID, allowance });

      try {
        const data = await createOrder({ payload, token }).unwrap();
        // A 201 with no body would leave the confirmation with no reference to
        // show, which reads as an order that may not have been placed.
        if (!data?.reference) {
          return { error: describeOrderError({ status: 500 }) };
        }
        return { order: mapBriefDetail(data) };
      } catch (error) {
        const described = describeOrderError(error);
        return {
          error: described,
          // The tags are already invalidated by the mutation, so the card
          // behind the modal is refetching. `conflict` tells the modal to wait
          // for those fresh numbers before letting the customer confirm again.
          conflict: described.kind === "conflict",
          // TIP-655 — and these ARE those fresh numbers, straight from the 409
          // body, so the form does not have to wait for the refetch to describe
          // itself honestly. The modal already reads `result.allowance`; it was
          // never sent, so it always fell back to the stale prop.
          allowance: described.allowance ?? null,
        };
      }
    },
    [companyID, token, createOrder]
  );

  return { submit, isSubmitting: isLoading };
};

/**
 * TIP-611 — the requester's own details, for section 1 of the order form.
 *
 * Two reads the app already owns: /api/user/me/ for the person, and the
 * selected company's details for the business they are acting as. Neither is
 * requested until the order form is actually open, which is what `enabled` is
 * for — the dashboard should not pull a company profile to draw an allowance
 * card.
 */
export const useRequesterProfile = ({ enabled = true } = {}) => {
  const companyID = useSelector(selectActiveCompanyId);
  const token = useSelector((s) => s.auth?.token);

  const user = useGetUserQuery(token, { skip: !enabled || !token });
  const company = useGetOptimisedCompanyDetailsQuery(
    { companyId: companyID, token },
    { skip: !enabled || !companyID }
  );

  return useMemo(
    () => ({
      profile: mapRequesterProfile({ user: user.data, company: company.data }),
      // The form opens straight away and adopts these as they arrive, so this
      // gates nothing — it only lets the section say it is still filling in.
      isLoading: (enabled && user.isLoading) || (enabled && company.isLoading),
      // A failed read is not an error the customer needs to see here: the
      // fields are simply empty and the required ones stop the submit until
      // they are filled by hand.
      error: user.error || company.error || null,
    }),
    [user.data, user.isLoading, user.error, company.data, company.isLoading, company.error, enabled]
  );
};

/**
 * TIP-611 — the reference contact lookup, as a plain async function.
 *
 *   GET /api/briefs/requester-lookup/?email=<email>
 *
 * The order form and its panels take everything as props and touch no store,
 * which is what lets them be rendered in a test without a Provider. So this
 * hands DOWN a function rather than the form reaching UP for a hook: the page
 * owns the store access, useOrderForm owns when to call it.
 *
 * Three outcomes, and the caller has to be able to tell them apart:
 *
 *   { found: true, contact }  a match — fill and lock the fields it carried
 *   { found: false }          a 404, or a 200 with nothing in it. Not an error:
 *                             the panel guesses the name and says it guessed
 *   { failed: true }          the request itself did not complete. The panel
 *                             behaves as for no match, because a contact the
 *                             customer can type by hand must not be blocked on
 *                             a directory being reachable
 */
export const useReferenceLookup = () => {
  const token = useSelector((s) => s.auth?.token);
  const [trigger] = useLazyLookupBriefRequesterQuery();

  return useCallback(
    async (email, { fallbackDial } = {}) => {
      // The email is the whole query — the endpoint takes no company_id, so
      // there is nothing else to be missing.
      if (!email) return { found: false };

      try {
        // `.unwrap()` throws on a 404, which is how the miss arrives.
        const data = await trigger({ email, token }).unwrap();
        const contact = mapRequesterLookup(data, { fallbackDial });
        return contact ? { found: true, contact } : { found: false };
      } catch (err) {
        // 404 is the documented "we do not know this address".
        if (err?.status === 404) return { found: false };
        return { found: false, failed: true };
      }
    },
    [token, trigger]
  );
};

/**
 * "You already have a brief for this business."
 *
 * Asked when the customer LEAVES the subject-name box, not while typing: the
 * name is only meaningful once it is finished, and a request per keystroke
 * would ask about a dozen prefixes of one company.
 *
 * The list endpoint's `search` is a substring match, so it is used to NARROW
 * and the exact comparison happens here — see duplicateSubject.js. A hit is a
 * warning the customer can ignore; ordering a second brief on the same company
 * is allowed, so nothing about this blocks the submit.
 *
 * A failed request says nothing at all. Being unable to check is not the same
 * as knowing there is no earlier brief, and a wrong reassurance is worse than
 * silence.
 */
export const useDuplicateSubjectCheck = () => {
  const companyID = useSelector(selectActiveCompanyId);
  const token = useSelector((s) => s.auth?.token);
  const [trigger] = useLazyGetBriefsListQuery();

  return useCallback(
    async (name) => {
      if (!isSearchable(name) || !isClaimedCompanyId(companyID)) return null;

      try {
        const data = await trigger(
          { companyID, token, page: 1, search: name.trim() },
          // `true` — prefer a cached answer. Tabbing back and forth through the
          // form must not re-ask for a name already checked.
          true
        ).unwrap();
        return duplicateWarning(data?.briefs, name);
      } catch {
        return null;
      }
    },
    [companyID, token, trigger]
  );
};

/**
 * TIP-610 — download the delivered PDF.
 *
 *   GET /api/briefs/orders/<reference>/download/
 *
 * The endpoint returns the FILE, so this cannot be an <a href>: the request
 * needs the Authorization header, and the masked S3 token must never reach
 * the page. The bytes come back and are handed to the browser as a blob.
 *
 * `downloadingRef` is the reference currently in flight rather than a plain
 * boolean, so only the row that was clicked shows itself working — a customer
 * with four ready briefs must not see all four buttons change.
 *
 * A failure that means the row is STALE (the server says not ready, or the
 * file is gone) refetches the list. The customer was looking at a Download
 * button that should not have been there, and apologising without correcting
 * the row would leave them clicking it again.
 */
export const useDownloadBrief = ({ onStale } = {}) => {
  const token = useSelector((s) => s.auth?.token);
  const [trigger] = useDownloadBriefOrderMutation();
  const [downloadingRef, setDownloadingRef] = useState(null);
  const [error, setError] = useState(null);

  const download = useCallback(
    async (brief) => {
      const reference = brief?.reference || brief?.id;
      if (!reference) return { ok: false };

      setError(null);
      setDownloadingRef(reference);
      try {
        const { blob, disposition } = await trigger({ reference, token }).unwrap();
        // A zero-byte body is not a PDF. Saving it would hand the customer a
        // file that opens as nothing and looks like their own fault.
        if (!blob || blob.size === 0) {
          setError({ message: TABLE_COPY.downloadError.failed, retryable: true });
          return { ok: false };
        }
        // Named from the ROW, not from the server's header: the target company
        // and the delivery date and time, spaces as underscores.
        const filename = saveBlob(blob, buildDownloadFilename({ brief, disposition }));
        return { ok: true, filename };
      } catch (err) {
        const described = describeDownloadError(err);
        setError(described);
        if (described.staleRow && onStale) onStale();
        return { ok: false };
      } finally {
        setDownloadingRef(null);
      }
    },
    [token, trigger, onStale]
  );

  return {
    download,
    downloadingRef,
    error,
    dismissError: useCallback(() => setError(null), []),
  };
};

// TIP-614 — how long to wait for the webhook before saying so.
//
// Stripe returns the customer to ?purchase=<session> the moment they confirm,
// but the credit happens in a webhook we do not control the timing of. Polled
// every two seconds for a minute; after that the customer is told the payment
// landed and the briefs are on their way, which is TRUE. A timeout is not a
// failure and must never be drawn as one — the alternative invites a second
// payment for briefs they already own.
const PURCHASE_POLL_MS = 2000;
const PURCHASE_POLL_TIMEOUT_MS = 60000;

/** The statuses that mean the purchase is settled, one way or another. */
const SETTLED = ["paid", "failed", "refunded", "canceled"];

/**
 * TIP-614 — buying extra briefs.
 *
 * Two jobs, because they are two halves of one flow separated by a redirect:
 *
 *   createSession  the modal's `Continue to payment`. Returns a client_secret
 *                  for Embedded Checkout, or a described error. NOTHING is
 *                  charged by this call.
 *   the poll       on the way back. Stripe's return_url is
 *                  /dashboard/counterparty-briefs?purchase=<session_id>, so a
 *                  session id in the query means "somebody just paid" and the
 *                  bought bucket is asked about until it agrees.
 *
 * Quantity is the only thing sent. Tier and price are the backend's to decide,
 * so a tampered client cannot buy at the member price.
 */
export const useBuyBriefs = () => {
  const companyID = useSelector(selectActiveCompanyId);
  // The prices, as the last allowance response reported them for THIS company.
  // Null until that lands, and null again on a company switch — the checkout
  // draws no figures rather than the previous company's.
  const pricing = useSelector((s) => selectBriefPricing(s, companyID));
  // Where Stripe will send the invoice. The backend sets `customer_email` on
  // the session from the authenticated user, so this is the same address —
  // read here only so the summary can say so before the customer pays.
  const receiptEmail = useSelector((s) => s.auth?.userInfo?.email || null);
  const token = useSelector((s) => s.auth?.token);
  const { search } = useLocation();
  const [createCheckout] = useCreateBriefCheckoutSessionMutation();
  const [readPurchase] = useLazyGetBriefPurchaseStateQuery();

  const sessionId = useMemo(
    () => new URLSearchParams(search).get("purchase") || null,
    [search]
  );

  // The session Stripe has just reported complete IN PLACE (no redirect).
  // With redirect_on_completion='never' there is no ?purchase=<id> to read it
  // back from, so the modal hands it over and the same poll below runs.
  const [paidSessionId, setPaidSessionId] = useState(null);
  const markPaid = useCallback((id) => setPaidSessionId(id || null), []);

  const [purchase, setPurchase] = useState(null);
  // "crediting" while we are still asking, "slow" once the minute is up.
  // Collapsing the two made a healthy two-second wait read as a problem.
  const [purchaseState, setPurchaseState] = useState("idle");

  const createSession = useCallback(
    async ({ quantity }) => {
      if (!companyID) {
        return { error: { message: "Select a business before buying briefs." } };
      }
      try {
        const data = await createCheckout({ companyID, quantity, token }).unwrap();
        if (!data?.client_secret) {
          // A 200 with no secret is not something to mount Stripe against.
          return { error: { message: ORDER_SUPPORT_FALLBACK } };
        }
        return { clientSecret: data.client_secret, sessionId: data.session_id || null };
      } catch (err) {
        // The backend words its own refusals and mints a support reference on
        // a Stripe failure; both are shown rather than replaced, so a customer
        // on the phone can quote it.
        return {
          error: {
            message: err?.data?.detail || ORDER_SUPPORT_FALLBACK,
            supportRef: err?.data?.support_ref || null,
            code: err?.data?.code || null,
          },
        };
      }
    },
    [companyID, token, createCheckout]
  );

  // The return leg. Runs only when Stripe has sent the customer back.
  useEffect(() => {
    // Either leg: the redirect's query param, or an in-place completion.
    const activeSessionId = sessionId || paidSessionId;
    if (!activeSessionId || !token) return undefined;

    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    setPurchaseState("crediting");
    setPurchase(null);

    const tick = async () => {
      if (cancelled) return;
      try {
        const data = await readPurchase({
          sessionId: activeSessionId,
          companyID,
          token,
        }).unwrap();
        if (cancelled) return;

        if (data?.status && SETTLED.includes(data.status)) {
          setPurchaseState("settled");
          // Only a paid purchase is a success panel. A failed or refunded one
          // credits nothing, and saying "briefs added" would be false.
          if (data.status === "paid") {
            setPurchase({
              quantity: Number(data.quantity) || 0,
              chargedTotalFils: Number(data.charged_total_fils) || 0,
              status: data.status,
              // Not in the response yet — the state endpoint returns only
              // status, quantity and charged_total_fils. The design's success
              // panel draws both, so the rows are rendered empty/disabled
              // rather than hidden, and fill themselves the moment the
              // backend sends these two names. See the note in BuyBriefsModal.
              reference: data.reference || null,
              receiptUrl: data.receipt_url || null,
            });
          }
          return;
        }
      } catch (err) {
        // A failed poll is not a failed payment. Keep asking until the
        // window closes, then fall back to the honest "on its way" panel.
        if (cancelled) return;
      }

      if (Date.now() - startedAt >= PURCHASE_POLL_TIMEOUT_MS) {
        // The payment happened; the credit has not caught up. The copy
        // changes, the reassurance does not.
        setPurchaseState("slow");
        return;
      }
      timer = setTimeout(tick, PURCHASE_POLL_MS);
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, paidSessionId, companyID, token, readPurchase]);

  const dismissPurchase = useCallback(() => {
    setPurchase(null);
    setPurchaseState("idle");
    // Or the poll effect re-fires the moment the modal is reopened.
    setPaidSessionId(null);
  }, []);

  return {
    createSession,
    // Told by the modal when Stripe completes without a redirect.
    markPaid,
    // Straight from the store, so the modal never holds its own copy.
    pricing,
    receiptEmail,
    // A session id in the URL is what re-opens the modal after the redirect.
    returnedFromCheckout: !!sessionId,
    purchase,
    // Still asking. Shows "Adding them to your balance…".
    purchaseCrediting: purchaseState === "crediting",
    // Asked for a minute, still nothing. Shows "within a minute".
    purchasePending: purchaseState === "slow",
    dismissPurchase,
  };
};

/**
 * TIP-613 — the brief-ready email's deep link.
 *
 * The email's CTA is
 *   {FRONTEND_APP_URL}/dashboard/counterparty-briefs?ref=TRX-CB-00007
 * (briefs/tasks.py `_deep_link_url`), and its job is to put the customer in
 * front of that one brief with Download ready. No token: the route is
 * auth-gated, and PrivateRoute already stores the full path — query string
 * included — as `lastVisited`, so a signed-out customer logs in and lands
 * back here rather than on the dashboard.
 *
 * Three steps, because a reference alone is not enough to open anything:
 *
 *   1. resolve/ says which company owns it. The customer may act for several,
 *      and the brief is only readable while that one is active.
 *   2. switch the active company if it is not already the right one. The name
 *      comes from the individual read the company switcher itself uses, so
 *      the header cannot end up showing a different company from the page.
 *   3. read the brief and hand back a row the status modal can open.
 *
 * A reference we cannot open — unknown, or another company's — is reported as
 * one thing. Distinguishing "does not exist" from "not yours" would tell a
 * stranger which references are real.
 */
export const useBriefDeepLink = ({ enabled = true } = {}) => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const token = useSelector((s) => s.auth?.token);
  const activeCompanyID = useSelector(selectActiveCompanyId);

  const reference = useMemo(() => {
    const raw = new URLSearchParams(search).get("ref");
    return raw ? raw.trim() : null;
  }, [search]);

  const [resolve] = useLazyResolveBriefReferenceQuery();
  const [state, setState] = useState("idle");
  const [ownerCompanyID, setOwnerCompanyID] = useState(null);

  // The user's own companies, with names — the same read, keyed the same way,
  // as the company switcher in the header. Taking the list from here rather
  // than from a company-details lookup means the object we put into
  // activeCompany has the shape the switcher puts there, so the header cannot
  // end up describing the company differently from the page.
  const individualID = useSelector((s) => s.auth?.userInfo?.individual?.[0]?.individualID);
  const individual = useGetIndividualQuery(
    { individualID, token },
    { skip: !enabled || !token || !reference || !individualID }
  );

  useEffect(() => {
    if (!enabled || !reference || !token) {
      setState("idle");
      return undefined;
    }

    let cancelled = false;
    setState("resolving");

    (async () => {
      try {
        const data = await resolve({ reference, token }).unwrap();
        if (cancelled) return;
        const owner = data?.company_id ?? null;
        if (!owner) {
          setState("unavailable");
          return;
        }
        setOwnerCompanyID(owner);
        setState("resolved");
      } catch (err) {
        if (cancelled) return;
        // 403 and 404 collapse on purpose — see the note above.
        setState(err?.status === 403 || err?.status === 404 ? "unavailable" : "failed");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, reference, token, resolve]);

  // Switch companies once, and only when it is actually a different one.
  useEffect(() => {
    if (state !== "resolved" || !ownerCompanyID) return;
    if (ownerCompanyID === activeCompanyID) return;

    const mine = (individual.data?.individual || [])
      .map((i) => ({
        companyID: i?.companyID?.companyID,
        name: i?.companyID?.registered_company_name,
        profile_status: i?.profile_status,
      }))
      .find((c) => c.companyID === ownerCompanyID);

    // Waiting on the list is fine; resolve/ has already said the user may see
    // this brief, so the company is one of theirs and will appear.
    if (mine) dispatch(setActiveCompany(mine));
  }, [state, ownerCompanyID, activeCompanyID, individual.data, dispatch]);

  const onTheRightCompany = state === "resolved" && ownerCompanyID === activeCompanyID;

  // The brief itself, once we are acting as the company that owns it.
  const detail = useGetBriefDetailQuery(
    { reference, token },
    { skip: !onTheRightCompany || !reference }
  );

  // A row shaped like the table's, so the status modal and its Download
  // button behave exactly as they do for a row the customer clicked.
  const row = useMemo(() => {
    if (!onTheRightCompany) return null;
    const mapped = mapBriefDetail(detail.data);
    if (!mapped || !mapped.reference || !mapped.status) return null;
    return {
      id: mapped.reference,
      reference: mapped.reference,
      company: mapped.company ?? "",
      country: mapped.country ?? "",
      status: mapped.status,
      ordered: mapped.ordered ?? null,
      completed: mapped.completed ?? null,
      orderedAt: mapped.orderedAt ?? null,
      completedAt: mapped.completedAt ?? null,
      downloadUrl: null,
      snagReason: mapped.snagReason ?? null,
    };
  }, [onTheRightCompany, detail.data]);

  return {
    reference,
    row,
    // Drawn as a quiet note on the page, not as a modal.
    unavailable: state === "unavailable",
    failed: state === "failed",
    // True while the link is being followed, so the page can hold off saying
    // "nothing here yet".
    following: !!reference && (state === "resolving" || (onTheRightCompany && detail.isLoading)),
  };
};

/**
 * TIP-615 — starting a membership checkout from the Briefs page.
 *
 * A SECOND ENTRY POINT, NOT A SECOND PRODUCT: it posts to the very endpoint
 * the dashboard's membership screen posts to, with the same `plan` values, so
 * the Stripe price IDs, the CompanyMembership row, the webhook handlers and
 * provisioning are all the existing ones. Nothing here is a parallel
 * membership implementation.
 *
 * `createVerifiedCheckoutSession` is reused rather than re-declared as an RTK
 * endpoint, so there is one definition of that request in the app.
 */
export const useMembershipCheckout = () => {
  const companyID = useSelector(selectActiveCompanyId);

  return useCallback(
    async ({ plan }) => {
      if (!companyID) {
        return { error: { message: "Select a business before joining." } };
      }
      try {
        // stayOnPage: the modal owns its own success step and re-reads the
        // allowance in place, so Stripe must not navigate away from
        // /dashboard/counterparty-briefs when the payment completes.
        const data = await createVerifiedCheckoutSession({
          companyID,
          plan,
          stayOnPage: true,
        });
        if (!data?.client_secret) {
          return { error: { message: MEMBERSHIP_COPY.errorBody } };
        }
        return { clientSecret: data.client_secret };
      } catch (err) {
        // 409 already_member is not a failure to report as one: the company
        // has what it was about to buy. TIP-615 requires the conflict path.
        if (err?.status === 409 || err?.data?.code === "already_member") {
          return { alreadyMember: true };
        }
        return { error: { message: err?.data?.detail || MEMBERSHIP_COPY.errorBody } };
      }
    },
    [companyID]
  );
};
